import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ChevronDown,
  ChevronRight,
  Hexagon,
  Info,
  MoreVertical,
  Plus,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";

import DeleteModal from "../../../../../Container/DeleteModal";
import SpiderCompareModalContent from "../../../../../intelligence-briefing-library/components/hawkeye/SpiderCompareModalContent";
import {
  Dialog,
  DialogContent,
} from "../../../../../intelligence-briefing-library/components/ui/dialog";
import type {
  AccountMatrixData,
  MetricsConfiguration,
} from "../../../../../intelligence-briefing-library/hooks/useHawkeyeMetrics";
import { useAppDispatch, useAppSelector } from "../../../../../Store/Hooks";
import { deleteMonitoredAccounts } from "../../../../../Store/Reducers/AlwaysOnChannelSlice";
import {
  deleteWatchList,
  type WatchListItem,
} from "../../../../../Store/Reducers/OpsSlice";
import {
  getWatchListAccountCount,
  getWatchListCustomerSponsorLabel,
  resolveWatchListCustomerId,
} from "../../utils/watchListAccounts";
import type { BirdsEyeClusterSummary } from "../BirdsEyeOverview";
import PortfolioSponsorOnlyState from "../PortfolioSponsorOnlyState";

const MIN_COMPARE_ACCOUNTS = 2;
const MAX_COMPARE_ACCOUNTS = 4;
const COMPARE_COLORS = ["#7c3aed", "#2563eb", "#0d9488", "#d97706"];

const CLUSTER_COLOR_LEGEND = [
  {
    label: "Renewal Risk",
    color: "#ef4444",
    description: "Accounts with elevated renewal / health risk.",
  },
  {
    label: "High Adoption / Mixed",
    color: "#eab308",
    description: "Accounts with strong or mixed adoption signals.",
  },
  {
    label: "Strong Performers",
    color: "#22c55e",
    description: "Accounts performing well across key metrics.",
  },
  {
    label: "KPIs Data not available",
    color: "#94a3b8",
    description: "Accounts without Account Health data yet.",
  },
] as const;

export type MonitoredSidebarTab = "executive" | "watchlist" | "others";

interface MonitoredWatchlistSidebarProps {
  activeTab: MonitoredSidebarTab;
  onTabChange: (tab: MonitoredSidebarTab) => void;
  canAccessExecutiveTab?: boolean;
  filteredWatchLists: WatchListItem[];
  accountsList?: any[];
  executiveAccounts: Array<{
    id: string;
    name: string;
    sponsorLabel?: string;
    relationshipType?: string | null;
  }>;
  executiveAccountCount: number;
  executiveAccountsLoading?: boolean;
  executiveAccountsError?: string | null;
  selectedExecutiveAccountIds?: string[];
  othersAccounts: Array<{
    id: string;
    name: string;
    monitorId?: string;
    sponsorLabel?: string;
  }>;
  othersAccountCount: number;
  othersAccountsLoading?: boolean;
  othersAccountsError?: string | null;
  selectedMonitoredAccountIds?: string[];
  selectedWatchlistId: string | null;
  matrixData?: AccountMatrixData[];
  configuration?: MetricsConfiguration;
  orderedCategories?: string[];
  /** Accounts selected on Bird's Eye maps (Risk-Opportunity / Revenue). */
  mapHighlightedAccountIds?: string[];
  /** True when Insights parent tab is Bird's Eye. */
  birdsEyeMode?: boolean;
  birdsEyeClusterGroups?: BirdsEyeClusterSummary[];
  onSelectWatchlist: (watchlistId: string) => void;
  onSelectExecutiveAccount: (accountId: string) => void;
  onSelectMonitoredAccount: (accountId: string) => void;
  onSwitchToAnyPortfolio?: () => void;
  onCreateWatchlist: () => void;
  onEditWatchlist?: (watchList: WatchListItem) => void;
  onWatchlistDeleted?: (watchlistId: string) => void;
  onMonitoredAccountDeleted?: (accountId: string) => void;
  /** When true, sort KPI-available accounts first. When false, alphabetical. */
  showUnavailableKpiData?: boolean;
}

const MonitoredWatchlistSidebar: React.FC<MonitoredWatchlistSidebarProps> = ({
  activeTab,
  onTabChange,
  canAccessExecutiveTab = false,
  filteredWatchLists,
  accountsList = [],
  executiveAccounts,
  executiveAccountCount,
  executiveAccountsLoading = false,
  executiveAccountsError = null,
  selectedExecutiveAccountIds = [],
  othersAccounts,
  othersAccountCount,
  othersAccountsLoading = false,
  othersAccountsError = null,
  selectedMonitoredAccountIds = [],
  selectedWatchlistId,
  matrixData,
  configuration,
  orderedCategories,
  mapHighlightedAccountIds = [],
  birdsEyeMode = false,
  birdsEyeClusterGroups = [],
  onSelectWatchlist,
  onSelectExecutiveAccount,
  onSelectMonitoredAccount,
  onSwitchToAnyPortfolio,
  onCreateWatchlist,
  onEditWatchlist,
  onWatchlistDeleted,
  onMonitoredAccountDeleted,
  showUnavailableKpiData = true,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { loading: deleteWatchListLoading } = useAppSelector(
    state => state.ops.deleteWatchList
  );
  const { deleting: deleteMonitoredLoading } = useAppSelector(
    state => state.alwaysOnChannel.monitorAccounts
  );

  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );

  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [compareSelectedIds, setCompareSelectedIds] = useState<string[]>([]);
  const [compareMessage, setCompareMessage] = useState<string | null>(null);
  const [expandedWatchlistIds, setExpandedWatchlistIds] = useState<Set<string>>(
    () => new Set()
  );
  const [groupAccountsByRisk, setGroupAccountsByRisk] = useState(false);
  const [clusterInfoOpen, setClusterInfoOpen] = useState(false);
  const [clusterInfoPos, setClusterInfoPos] = useState({ top: 0, left: 0 });
  const clusterInfoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const clusterInfoTriggerRef = useRef<HTMLButtonElement | null>(null);
  const clusterInfoTooltipRef = useRef<HTMLDivElement | null>(null);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [openMenuRect, setOpenMenuRect] = useState<DOMRect | null>(null);
  const [watchlistToDelete, setWatchlistToDelete] =
    useState<WatchListItem | null>(null);
  const [monitoredAccountToDelete, setMonitoredAccountToDelete] = useState<{
    id: string;
    name: string;
    monitorId: string;
  } | null>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  const closeRowMenu = useCallback(() => {
    setOpenMenuKey(null);
    setOpenMenuRect(null);
  }, []);

  const openClusterInfo = useCallback(() => {
    if (clusterInfoCloseTimerRef.current) {
      clearTimeout(clusterInfoCloseTimerRef.current);
      clusterInfoCloseTimerRef.current = null;
    }
    setClusterInfoOpen(true);
  }, []);

  const scheduleCloseClusterInfo = useCallback(() => {
    if (clusterInfoCloseTimerRef.current) {
      clearTimeout(clusterInfoCloseTimerRef.current);
    }
    clusterInfoCloseTimerRef.current = setTimeout(() => {
      setClusterInfoOpen(false);
      clusterInfoCloseTimerRef.current = null;
    }, 200);
  }, []);

  const updateClusterInfoPosition = useCallback(() => {
    const trigger = clusterInfoTriggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const tipWidth = clusterInfoTooltipRef.current?.offsetWidth ?? 280;
    const tipHeight = clusterInfoTooltipRef.current?.offsetHeight ?? 180;
    const gap = 8;
    const padding = 8;

    let left = rect.left + rect.width / 2 - tipWidth / 2;
    let top = rect.bottom + gap;

    left = Math.min(left, window.innerWidth - tipWidth - padding);
    left = Math.max(padding, left);

    if (top + tipHeight > window.innerHeight - padding) {
      top = rect.top - tipHeight - gap;
    }
    top = Math.max(padding, top);

    setClusterInfoPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!clusterInfoOpen) return;
    updateClusterInfoPosition();
    const frame = requestAnimationFrame(() => updateClusterInfoPosition());
    return () => cancelAnimationFrame(frame);
  }, [clusterInfoOpen, updateClusterInfoPosition]);

  useEffect(() => {
    if (!clusterInfoOpen) return;
    const onReposition = () => updateClusterInfoPosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [clusterInfoOpen, updateClusterInfoPosition]);

  useEffect(() => {
    return () => {
      if (clusterInfoCloseTimerRef.current) {
        clearTimeout(clusterInfoCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!openMenuKey) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuContainerRef.current?.contains(target)) return;
      const trigger = document.querySelector(
        `[data-monitored-menu-trigger="${CSS.escape(openMenuKey)}"]`
      );
      if (trigger?.contains(target)) return;
      closeRowMenu();
    };

    const handleCloseOnScroll = () => closeRowMenu();

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("scroll", handleCloseOnScroll, true);
    window.addEventListener("resize", handleCloseOnScroll);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", handleCloseOnScroll, true);
      window.removeEventListener("resize", handleCloseOnScroll);
    };
  }, [closeRowMenu, openMenuKey]);

  const toggleRowMenu = useCallback(
    (menuKey: string, event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      setOpenMenuKey(prev => {
        if (prev === menuKey) {
          setOpenMenuRect(null);
          return null;
        }
        setOpenMenuRect(rect);
        return menuKey;
      });
    },
    []
  );

  const getFixedMenuStyle = useCallback(
    (width: number, height: number): React.CSSProperties | undefined => {
      if (!openMenuRect) return undefined;
      const openUp = window.innerHeight - openMenuRect.bottom < height + 8;
      return {
        position: "fixed",
        top: openUp ? openMenuRect.top - height - 4 : openMenuRect.bottom + 4,
        left: Math.min(
          Math.max(8, openMenuRect.right - width),
          window.innerWidth - width - 8
        ),
        zIndex: 9999,
        width,
      };
    },
    [openMenuRect]
  );

  const renderFixedMenu = useCallback(
    (
      menuKey: string,
      width: number,
      height: number,
      content: React.ReactNode
    ) => {
      if (openMenuKey !== menuKey || !openMenuRect) return null;
      return createPortal(
        <div
          ref={menuContainerRef}
          role="menu"
          style={getFixedMenuStyle(width, height)}
          className="overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {content}
        </div>,
        document.body
      );
    },
    [getFixedMenuStyle, openMenuKey, openMenuRect]
  );

  const handleDeleteWatchlistClick = useCallback(
    (watchList: WatchListItem, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setOpenMenuKey(null);
      setOpenMenuRect(null);
      setWatchlistToDelete(watchList);
    },
    []
  );

  const handleEditWatchlistClick = useCallback(
    (watchList: WatchListItem, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setOpenMenuKey(null);
      setOpenMenuRect(null);
      onEditWatchlist?.(watchList);
    },
    [onEditWatchlist]
  );

  const handleDeleteWatchlistConfirm = useCallback(async () => {
    if (!watchlistToDelete?.watch_list_id) return;

    const watchlistId = watchlistToDelete.watch_list_id;
    const result = await dispatch(deleteWatchList(watchlistId));
    if (result.meta.requestStatus === "fulfilled") {
      setWatchlistToDelete(null);
      onWatchlistDeleted?.(watchlistId);
    }
  }, [dispatch, onWatchlistDeleted, watchlistToDelete]);

  const handleDeleteMonitoredClick = useCallback(
    (
      account: { id: string; name: string; monitorId?: string },
      event: React.MouseEvent
    ) => {
      event.stopPropagation();
      event.preventDefault();
      if (!account.monitorId) return;
      setOpenMenuKey(null);
      setOpenMenuRect(null);
      setMonitoredAccountToDelete({
        id: account.id,
        name: account.name,
        monitorId: account.monitorId,
      });
    },
    []
  );

  const handleDeleteMonitoredConfirm = useCallback(async () => {
    if (!monitoredAccountToDelete?.monitorId) return;

    const accountId = monitoredAccountToDelete.id;
    const result = await dispatch(
      deleteMonitoredAccounts([monitoredAccountToDelete.monitorId])
    );
    if (result.meta.requestStatus === "fulfilled") {
      setMonitoredAccountToDelete(null);
      onMonitoredAccountDeleted?.(accountId);
    }
  }, [dispatch, monitoredAccountToDelete, onMonitoredAccountDeleted]);

  const handleOpenAccountSynopsis = useCallback(
    (accountId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (!accountId) return;
      setOpenMenuKey(null);
      setOpenMenuRect(null);
      navigate(withOrg(`/account-synopsis/${accountId}`), {
        state: {
          from: withOrg("/always-on-channel"),
          fromLabel: "Always On Intelligence",
        },
      });
    },
    [navigate, withOrg]
  );

  const handleOpenAccountOverview = useCallback(
    (accountId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (!accountId) return;
      setOpenMenuKey(null);
      setOpenMenuRect(null);
      navigate(withOrg(`/account-workspace/${accountId}`), {
        state: {
          from: withOrg("/always-on-channel"),
          fromLabel: "Always On Intelligence",
        },
      });
    },
    [navigate, withOrg]
  );

  const renderAccountNavMenuItems = useCallback(
    (accountId: string) => (
      <>
        <button
          type="button"
          role="menuitem"
          onClick={event => handleOpenAccountSynopsis(accountId, event)}
          className="block w-full px-3 py-2 text-left text-[12px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          Account Synopsis
        </button>
        <button
          type="button"
          role="menuitem"
          onClick={event => handleOpenAccountOverview(accountId, event)}
          className="block w-full px-3 py-2 text-left text-[12px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          Account Workspace
        </button>
      </>
    ),
    [handleOpenAccountOverview, handleOpenAccountSynopsis]
  );

  const toggleWatchlistExpanded = useCallback(
    (watchlistId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      setExpandedWatchlistIds(prev => {
        const next = new Set(prev);
        if (next.has(watchlistId)) {
          next.delete(watchlistId);
        } else {
          next.add(watchlistId);
        }
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (!selectedWatchlistId) return;
    setExpandedWatchlistIds(prev => {
      if (prev.has(selectedWatchlistId)) return prev;
      const next = new Set(prev);
      next.add(selectedWatchlistId);
      return next;
    });
  }, [selectedWatchlistId]);

  const accountsById = useMemo(
    () =>
      new Map(
        accountsList.map(account => [
          String(account.customer_id || account.id || "").trim(),
          account,
        ])
      ),
    [accountsList]
  );

  const accountsByName = useMemo(
    () =>
      new Map(
        accountsList.map(account => [
          String(account.name || account.account_name || "")
            .trim()
            .toLowerCase(),
          account,
        ])
      ),
    [accountsList]
  );

  const getAccountNameForSort = (account: any) =>
    String(account?.name || account?.account_name || "").trim();

  const getSponsorNameForSort = (account: any) =>
    String(account?.primary_sponsors || "").trim() || "Unassigned";

  const isUnassignedForSort = (account: any) => {
    const primarySponsorId = String(account?.primary_sponsors_id || "").trim();
    const secondarySponsorIds = account?.secondary_sponsors_id
      ? String(account.secondary_sponsors_id)
          .split(",")
          .map((id: string) => id.trim())
          .filter(Boolean)
      : [];
    return !primarySponsorId && secondarySponsorIds.length === 0;
  };

  const digestAccounts = useMemo(
    () => (matrixData || []).filter(account => account.hasDigest),
    [matrixData]
  );

  const accountsWithoutKpiDataSet = useMemo(
    () =>
      new Set(
        (matrixData || [])
          .filter(account => account.hasData === false)
          .map(account => String(account.account_id))
      ),
    [matrixData]
  );

  const isWithoutKpiDataForSort = (account: any) =>
    accountsWithoutKpiDataSet.has(
      String(account?.customer_id || account?.id || account?.account_id || "")
    );

  const compareAccountsByKpiThenSponsor = (left: any, right: any) => {
    if (showUnavailableKpiData) {
      const leftNoData = isWithoutKpiDataForSort(left);
      const rightNoData = isWithoutKpiDataForSort(right);
      if (leftNoData !== rightNoData) {
        return leftNoData ? 1 : -1;
      }

      const leftUnassigned = isUnassignedForSort(left);
      const rightUnassigned = isUnassignedForSort(right);
      if (leftUnassigned !== rightUnassigned) {
        return leftUnassigned ? -1 : 1;
      }

      const sponsorCompare = getSponsorNameForSort(left).localeCompare(
        getSponsorNameForSort(right),
        undefined,
        { sensitivity: "base" }
      );
      if (sponsorCompare !== 0) {
        return sponsorCompare;
      }
    }

    return getAccountNameForSort(left).localeCompare(
      getAccountNameForSort(right),
      undefined,
      { sensitivity: "base" }
    );
  };

  const sortedExecutiveAccounts = useMemo(
    () => [...executiveAccounts].sort(compareAccountsByKpiThenSponsor),
    [executiveAccounts, accountsWithoutKpiDataSet, showUnavailableKpiData]
  );

  const sortedOthersAccounts = useMemo(
    () => [...othersAccounts].sort(compareAccountsByKpiThenSponsor),
    [othersAccounts, accountsWithoutKpiDataSet, showUnavailableKpiData]
  );

  const spiderAccounts = useMemo(
    () =>
      digestAccounts.map(account => ({
        account_id: account.account_id,
        account_name: account.account_name,
        overallScore: account.overallScore,
        hasData: account.hasData,
        categoryScores: account.categoryScores.map(score => ({
          category: score.category,
          score: score.score,
        })),
      })),
    [digestAccounts]
  );

  const categoryOrder = useMemo(() => {
    if (orderedCategories?.length) return orderedCategories;
    const included = configuration?.includedCategories;
    if (included?.length) return included;
    const first = digestAccounts.find(
      account => account.categoryScores?.length
    );
    return first?.categoryScores.map(score => score.category) ?? [];
  }, [orderedCategories, configuration, digestAccounts]);

  const canCompare =
    activeTab === "watchlist" &&
    digestAccounts.length >= MIN_COMPARE_ACCOUNTS &&
    categoryOrder.length > 0;

  const compareTitle = useMemo(() => {
    if (canCompare) return "Compare accounts in this watchlist (spider view)";
    if (digestAccounts.length < MIN_COMPARE_ACCOUNTS) {
      return `At least ${MIN_COMPARE_ACCOUNTS} accounts with digest data are required to compare`;
    }
    return "Comparison is not available for the current data";
  }, [canCompare, digestAccounts.length]);

  useEffect(() => {
    if (!isCompareOpen) {
      setCompareMessage(null);
      return;
    }
    const count = compareSelectedIds.length;
    if (count === 0) {
      setCompareMessage(null);
      return;
    }
    if (count < MIN_COMPARE_ACCOUNTS) {
      setCompareMessage("Select at least 2 accounts to compare.");
      return;
    }
    if (count > MAX_COMPARE_ACCOUNTS) {
      setCompareMessage("Maximum 4 accounts can be compared.");
      return;
    }
    setCompareMessage(null);
  }, [isCompareOpen, compareSelectedIds.length]);

  const handleCompareOpenChange = useCallback((open: boolean) => {
    setIsCompareOpen(open);
    if (!open) {
      setCompareSelectedIds([]);
      setCompareMessage(null);
    }
  }, []);

  const toggleCompareAccount = useCallback(
    (accountId: string) => {
      setCompareSelectedIds(prev => {
        if (prev.includes(accountId)) {
          if (isCompareOpen && prev.length <= MIN_COMPARE_ACCOUNTS) {
            setCompareMessage(
              "At least 2 accounts are required in comparison."
            );
            return prev;
          }
          return prev.filter(id => id !== accountId);
        }
        if (prev.length >= MAX_COMPARE_ACCOUNTS) {
          setCompareMessage("You can compare up to 4 accounts only.");
          return prev;
        }
        return [...prev, accountId];
      });
    },
    [isCompareOpen]
  );

  const handleOpenCompare = useCallback(() => {
    if (!canCompare) return;
    setCompareSelectedIds(
      digestAccounts
        .slice(0, MAX_COMPARE_ACCOUNTS)
        .map(account => account.account_id)
    );
    setCompareMessage(null);
    setIsCompareOpen(true);
  }, [canCompare, digestAccounts]);

  const mapHighlightIdSet = useMemo(
    () => new Set(mapHighlightedAccountIds.map(id => String(id).trim())),
    [mapHighlightedAccountIds]
  );

  const accountClusterColorById = useMemo(() => {
    const colorById = new Map<string, string>();
    birdsEyeClusterGroups.forEach(group => {
      group.accounts.forEach(account => {
        if (!account.accountId) return;
        colorById.set(account.accountId, group.accentColor);
      });
    });
    return colorById;
  }, [birdsEyeClusterGroups]);

  const renderAccountNameWithDot = (
    name: string,
    accountId?: string,
    className = "min-w-0 truncate px-3 py-2 font-medium",
    showDot = false
  ) => (
    <span className={`inline-flex min-w-0 items-center gap-2 ${className}`}>
      {showDot ? (
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{
            backgroundColor: accountId
              ? accountClusterColorById.get(accountId) || "#94a3b8"
              : "#94a3b8",
          }}
          aria-hidden
        />
      ) : null}
      <span className="min-w-0 truncate">{name}</span>
    </span>
  );

  const renderExecutiveTab = () => {
    if (!canAccessExecutiveTab) {
      return (
        <PortfolioSponsorOnlyState
          variant="sidebar"
          title="Executive view for C-level, Executive and Sponsors."
          onSwitchToAny={onSwitchToAnyPortfolio}
        />
      );
    }

    return (
      <div className="rounded-lg border border-[#e5e9ef] bg-white px-2 py-2.5">
        {executiveAccountsLoading ? (
          <div className="px-2 py-3 text-sm text-gray-500">
            Loading extra sponsor accounts...
          </div>
        ) : executiveAccountsError ? (
          <div className="px-2 py-3 text-sm text-gray-500">
            No extra sponsor accounts found.
          </div>
        ) : executiveAccounts.length > 0 ? (
          <>
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem] gap-2 border-b border-[#dedee6] px-2 pb-2 text-[11px] font-semibold text-[#384a63]">
              <span>Account Name</span>
              <span>Primary Sponsor</span>
              <span className="text-center">Action</span>
            </div>
            <div className="mt-1.5 space-y-1.5">
              {sortedExecutiveAccounts.map(account => {
                const isMapHighlighted = mapHighlightIdSet.has(account.id);
                const isSidebarSelected = selectedExecutiveAccountIds.includes(
                  account.id
                );
                const menuKey = `executive:${account.id}`;
                const isMenuOpen = openMenuKey === menuKey;

                return (
                  <div
                    key={account.id}
                    className={`relative grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem] items-center gap-2 rounded-md border text-[13px] transition-colors ${
                      isSidebarSelected
                        ? "border-[#c4b5fd] bg-[#f5f5fa] text-[#111827]"
                        : isMapHighlighted
                          ? "border-[#c4b5fd] bg-[#ede9fe] text-[#5b21b6]"
                          : "border-transparent bg-[#f5f5fa] text-[#111827] hover:border-[#ece3fb]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectExecutiveAccount(account.id)}
                      className="min-w-0 truncate px-3 py-2 text-left font-medium"
                    >
                      {account.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => onSelectExecutiveAccount(account.id)}
                      className="min-w-0 truncate px-1 py-2 text-left text-[12px] text-[#4b5563]"
                    >
                      {account.sponsorLabel || "Unassigned"}
                    </button>
                    <div className="relative shrink-0 pr-1">
                      <button
                        type="button"
                        data-monitored-menu-trigger={menuKey}
                        aria-label={`Actions for ${account.name}`}
                        aria-haspopup="menu"
                        aria-expanded={isMenuOpen}
                        onClick={event => toggleRowMenu(menuKey, event)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-[#e8eef7] hover:text-slate-700"
                      >
                        <MoreVertical size={14} strokeWidth={2.25} />
                      </button>
                      {renderFixedMenu(
                        menuKey,
                        150,
                        88,
                        renderAccountNavMenuItems(account.id)
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="px-2 py-3 text-sm text-gray-500">
            {executiveAccountCount > 0
              ? "No extra sponsor accounts match your search"
              : "No extra sponsor accounts found."}
          </div>
        )}
      </div>
    );
  };

  const renderOthersTab = () => (
    <div className="rounded-lg border border-[#e5e9ef] bg-white px-2 py-2.5">
      {othersAccountsLoading ? (
        <div className="px-2 py-3 text-sm text-gray-500">
          Loading monitored accounts...
        </div>
      ) : othersAccountsError ? (
        <div className="px-2 py-3 text-sm text-gray-500">
          No monitored accounts found.
        </div>
      ) : othersAccounts.length > 0 ? (
        <>
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem] gap-2 border-b border-[#dedee6] px-2 pb-2 text-[11px] font-semibold text-[#384a63]">
            <span>Account Name</span>
            <span>Primary Sponsor</span>
            <span className="text-center">Action</span>
          </div>
          <div className="mt-1.5 space-y-1.5">
            {sortedOthersAccounts.map(account => {
              const isMapHighlighted = mapHighlightIdSet.has(account.id);
              const isSidebarSelected = selectedMonitoredAccountIds.includes(
                account.id
              );
              const menuKey = `others:${account.id}`;
              const isMenuOpen = openMenuKey === menuKey;
              const canDelete = Boolean(account.monitorId);

              return (
                <div
                  key={account.id}
                  className={`relative grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem] items-center gap-2 rounded-md border text-[13px] transition-colors ${
                    isSidebarSelected
                      ? "border-[#c4b5fd] bg-[#f5f5fa] text-[#111827]"
                      : isMapHighlighted
                        ? "border-[#c4b5fd] bg-[#ede9fe] text-[#5b21b6]"
                        : "border-transparent bg-[#f5f5fa] text-[#111827] hover:border-[#ece3fb]"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onSelectMonitoredAccount(account.id)}
                    className="min-w-0 truncate px-3 py-2 text-left font-medium"
                  >
                    {account.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => onSelectMonitoredAccount(account.id)}
                    className="min-w-0 truncate px-1 py-2 text-left text-[12px] text-[#4b5563]"
                  >
                    {account.sponsorLabel || "Unassigned"}
                  </button>
                  <div className="relative shrink-0 pr-1">
                    <button
                      type="button"
                      data-monitored-menu-trigger={menuKey}
                      aria-label={`Actions for ${account.name}`}
                      aria-haspopup="menu"
                      aria-expanded={isMenuOpen}
                      onClick={event => toggleRowMenu(menuKey, event)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-[#e8eef7] hover:text-slate-700"
                    >
                      <MoreVertical size={14} strokeWidth={2.25} />
                    </button>
                    {renderFixedMenu(
                      menuKey,
                      150,
                      canDelete ? 120 : 88,
                      <>
                        {renderAccountNavMenuItems(account.id)}
                        {canDelete ? (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={event =>
                              handleDeleteMonitoredClick(account, event)
                            }
                            className="block w-full px-3 py-2 text-left text-[12px] font-medium text-red-600 transition-colors hover:bg-red-50"
                          >
                            Delete
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="px-2 py-3 text-sm text-gray-500">
          {othersAccountCount > 0
            ? "No monitored accounts match your search"
            : "No monitored accounts found."}
        </div>
      )}
    </div>
  );

  const renderWatchlistTab = () => (
    <>
      <div className="flex items-center justify-between gap-2 px-1">
        <div className="inline-flex min-w-0 items-center gap-1.5 text-[13px] font-semibold text-[#111827]">
          <span>Watchlists ({filteredWatchLists.length})</span>
        </div>
        <div className="inline-flex min-w-0 items-center gap-2 text-[#974ae4]">
          {birdsEyeMode ? (
            <div className="inline-flex h-7 shrink-0 items-center gap-1.5 px-1 py-1 text-[11px] font-semibold text-[#384a63]">
              <span>Show cluster</span>
              <button
                type="button"
                ref={clusterInfoTriggerRef}
                aria-label="What cluster colors mean"
                aria-expanded={clusterInfoOpen}
                onMouseEnter={openClusterInfo}
                onMouseLeave={scheduleCloseClusterInfo}
                onFocus={openClusterInfo}
                onBlur={scheduleCloseClusterInfo}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setClusterInfoOpen(prev => !prev);
                }}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#64748b] transition-colors hover:bg-[#efe3fa] hover:text-[#475569]"
              >
                <Info size={12} strokeWidth={2.25} />
              </button>
              <label
                className="inline-flex cursor-pointer items-center"
                title="Show cluster-colored dots on the active watchlist"
              >
                <input
                  type="checkbox"
                  role="switch"
                  checked={groupAccountsByRisk}
                  onChange={event =>
                    setGroupAccountsByRisk(event.target.checked)
                  }
                  className="peer sr-only"
                />
                <span
                  className={`relative inline-flex h-3.5 w-6 shrink-0 items-center rounded-full transition-colors ${
                    groupAccountsByRisk ? "bg-[#974ae4]" : "bg-[#cbd5e1]"
                  }`}
                  aria-hidden
                >
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full bg-white transition-transform ${
                      groupAccountsByRisk ? "translate-x-3" : "translate-x-0.5"
                    }`}
                  />
                </span>
              </label>
              {clusterInfoOpen &&
                createPortal(
                  <div
                    ref={clusterInfoTooltipRef}
                    role="tooltip"
                    onMouseEnter={openClusterInfo}
                    onMouseLeave={scheduleCloseClusterInfo}
                    className="z-[10050] w-[280px] rounded-lg border border-slate-200 bg-white p-3 shadow-lg"
                    style={{
                      position: "fixed",
                      top: clusterInfoPos.top,
                      left: clusterInfoPos.left,
                    }}
                  >
                    <p className="mb-2 text-[12px] font-semibold text-slate-800">
                      Dot colors by Bird&apos;s Eye cluster
                    </p>
                    <div className="space-y-2.5">
                      {CLUSTER_COLOR_LEGEND.map(item => (
                        <div
                          key={item.label}
                          className="flex items-start gap-2"
                        >
                          <span
                            className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: item.color }}
                            aria-hidden
                          />
                          <div className="min-w-0">
                            <p
                              className="text-[11px] font-semibold uppercase tracking-[0.02em]"
                              style={{ color: item.color }}
                            >
                              {item.label}
                            </p>
                            <p className="text-[11px] leading-snug text-slate-500">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>,
                  document.body
                )}
            </div>
          ) : null}
          <button
            type="button"
            onClick={handleOpenCompare}
            disabled={!canCompare}
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-[#c9a3f0] bg-[#f8fafc]  px-2 py-1 text-[12px] font-medium text-[#974ae4] transition-colors hover:bg-[#efe3fa] disabled:cursor-not-allowed disabled:opacity-50"
            title={compareTitle}
            aria-label="Compare watchlist accounts"
          >
            <Hexagon size={12} strokeWidth={2.5} className="shrink-0" />
            Compare
          </button>
          <button
            type="button"
            onClick={onCreateWatchlist}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[#c9a3f0] bg-[#f8fafc]  text-[#974ae4] transition-colors hover:bg-[#efe3fa]"
            title="Create watchlist"
            aria-label="Create watchlist"
          >
            <Plus size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-[#e5e9ef] bg-white px-2 py-2.5">
        {filteredWatchLists.length > 0 ? (
          <div className="space-y-3">
            {filteredWatchLists.map(watchList => {
              const watchlistId = watchList.watch_list_id;
              const isActive = selectedWatchlistId === watchlistId;
              const isExpanded = expandedWatchlistIds.has(watchlistId);
              const showAccountDots =
                birdsEyeMode && isActive && groupAccountsByRisk;
              const menuKey = `watchlist:${watchlistId}`;
              const isMenuOpen = openMenuKey === menuKey;
              const sortedCustomers = [...(watchList.customers || [])].sort(
                (left: any, right: any) => {
                  const leftSourceAccount =
                    accountsById.get(
                      String(left.customer_id || left.id || "").trim()
                    ) ||
                    accountsByName.get(
                      String(left.name || left.account_name || "")
                        .trim()
                        .toLowerCase()
                    ) ||
                    left;
                  const rightSourceAccount =
                    accountsById.get(
                      String(right.customer_id || right.id || "").trim()
                    ) ||
                    accountsByName.get(
                      String(right.name || right.account_name || "")
                        .trim()
                        .toLowerCase()
                    ) ||
                    right;

                  return compareAccountsByKpiThenSponsor(
                    leftSourceAccount,
                    rightSourceAccount
                  );
                }
              );

              const renderFlatAccountRows = () => (
                <div className="max-h-40 max-w-full space-y-1 overflow-y-auto">
                  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem] gap-2 border-b border-[#dedee6] px-2 pb-2 text-[11px] font-semibold text-[#384a63]">
                    <span>Account Name</span>
                    <span>Primary Sponsor</span>
                    <span className="text-center">Action</span>
                  </div>
                  {sortedCustomers.map((customer, index) => {
                    const customerId = resolveWatchListCustomerId(
                      customer,
                      accountsList
                    );
                    const sponsorLabel = getWatchListCustomerSponsorLabel(
                      customer,
                      accountsList
                    );
                    const isMapHighlighted =
                      customerId.length > 0 &&
                      mapHighlightIdSet.has(customerId);
                    const accountMenuKey = `watchlist-account:${watchlistId}:${customerId || index}`;
                    const isAccountMenuOpen = openMenuKey === accountMenuKey;

                    return (
                      <div
                        key={`${watchList.watch_list_id}-${customer.customer_id || customer.name || index}`}
                        className={`relative grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.75rem] items-center gap-1 rounded-md text-[12px] font-medium transition-colors ${
                          isMapHighlighted
                            ? "bg-[#f5edfc] text-[#974ae4] ring-1 ring-[#c9a3f0]"
                            : "bg-white text-[#111827]"
                        }`}
                      >
                        {renderAccountNameWithDot(
                          customer.name || "Unknown Account",
                          customerId || undefined,
                          "min-w-0 truncate px-2 py-2 font-medium",
                          showAccountDots
                        )}
                        <span className="min-w-0 truncate px-1 py-2 text-[11px] font-normal text-[#4b5563]">
                          {sponsorLabel}
                        </span>
                        {customerId ? (
                          <div className="relative shrink-0 pr-1">
                            <button
                              type="button"
                              data-monitored-menu-trigger={accountMenuKey}
                              aria-label={`Actions for ${customer.name || "account"}`}
                              aria-haspopup="menu"
                              aria-expanded={isAccountMenuOpen}
                              onClick={event =>
                                toggleRowMenu(accountMenuKey, event)
                              }
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-[#e8eef7] hover:text-slate-700"
                            >
                              <MoreVertical size={14} strokeWidth={2.25} />
                            </button>
                            {renderFixedMenu(
                              accountMenuKey,
                              150,
                              88,
                              renderAccountNavMenuItems(customerId)
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              );

              return (
                <div
                  key={watchlistId}
                  className={`overflow-hidden rounded-lg border ${
                    isActive
                      ? "border-[#974ae4] bg-white"
                      : "border-[#e5e9ef] bg-white"
                  }`}
                >
                  <div
                    className={`flex w-full items-center gap-1 text-[13px] font-medium ${
                      isActive
                        ? "bg-[#f5edfc] text-[#974ae4]"
                        : "bg-[#f8fafc] text-[#111827]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectWatchlist(watchlistId)}
                      className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2.5 text-left"
                    >
                      <span
                        className={`min-w-0 truncate ${
                          isActive
                            ? "font-semibold text-[#974ae4]"
                            : "font-medium text-[#111827]"
                        }`}
                      >
                        {watchList.watch_list_name} (
                        {getWatchListAccountCount(watchList)})
                      </span>
                    </button>
                    <div className="relative shrink-0">
                      <button
                        type="button"
                        data-monitored-menu-trigger={menuKey}
                        aria-label={`Actions for ${watchList.watch_list_name}`}
                        aria-haspopup="menu"
                        aria-expanded={isMenuOpen}
                        onClick={event => toggleRowMenu(menuKey, event)}
                        className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                          isActive
                            ? "text-[#974ae4] hover:bg-[#efe3fa]"
                            : "text-slate-500 hover:bg-[#e2e8f0]"
                        }`}
                      >
                        <MoreVertical size={14} strokeWidth={2.25} />
                      </button>
                      {renderFixedMenu(
                        menuKey,
                        120,
                        80,
                        <>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={event =>
                              handleEditWatchlistClick(watchList, event)
                            }
                            className="block w-full px-3 py-2 text-left text-[12px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={event =>
                              handleDeleteWatchlistClick(watchList, event)
                            }
                            className="block w-full px-3 py-2 text-left text-[12px] font-medium text-red-600 transition-colors hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded
                          ? `Collapse accounts in ${watchList.watch_list_name}`
                          : `Show accounts in ${watchList.watch_list_name}`
                      }
                      onClick={event =>
                        toggleWatchlistExpanded(watchlistId, event)
                      }
                      className={`shrink-0 rounded-md p-2.5 transition-colors ${
                        isActive
                          ? "text-[#974ae4] hover:bg-[#efe3fa]"
                          : "text-slate-500 hover:bg-[#e2e8f0]"
                      }`}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                      ) : (
                        <ChevronRight
                          className="h-4 w-4 shrink-0"
                          aria-hidden
                        />
                      )}
                    </button>
                  </div>

                  {isExpanded && sortedCustomers.length > 0 && (
                    <div className="border-t border-[#e5e9ef] bg-[#f8fafc] p-2">
                      {renderFlatAccountRows()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-2 py-3 text-sm text-gray-500">
            No watch lists match your search
          </div>
        )}
      </div>
    </>
  );

  const segmentTabs: Array<{
    id: MonitoredSidebarTab;
    label: string;
    count: number;
  }> = [
    { id: "executive", label: "Executive", count: executiveAccountCount },
    { id: "watchlist", label: "Watchlist", count: filteredWatchLists.length },
    { id: "others", label: "Others", count: othersAccountCount },
  ];

  return (
    <>
      <div className="space-y-3 pt-0.5">
        <div className="grid grid-cols-3 overflow-visible rounded-md border border-gray-200 bg-white text-[13px] font-medium">
          {segmentTabs.map((tab, index) => {
            const isActive = activeTab === tab.id;
            const isFirst = index === 0;
            const isLast = index === segmentTabs.length - 1;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={`px-1 py-2.5 transition-colors ${
                  isFirst ? "rounded-l-[5px]" : ""
                } ${isLast ? "rounded-r-[5px]" : ""} ${
                  isActive
                    ? "bg-[#f5edfc] font-semibold text-[#974ae4]"
                    : "bg-white text-[#4b5563] hover:bg-[#f3f4f6]"
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            );
          })}
        </div>

        {activeTab === "executive" && renderExecutiveTab()}
        {activeTab === "watchlist" && renderWatchlistTab()}
        {activeTab === "others" && renderOthersTab()}
      </div>

      <Dialog open={isCompareOpen} onOpenChange={handleCompareOpenChange}>
        <DialogContent
          hideClose
          className="flex max-h-[92vh] w-[95vw] max-w-[980px] flex-col overflow-hidden rounded-[14px] border border-slate-200 bg-white p-0 shadow-2xl"
        >
          <SpiderCompareModalContent
            allAccounts={spiderAccounts}
            selectedAccountIds={compareSelectedIds}
            categoryOrder={categoryOrder}
            compareColors={COMPARE_COLORS}
            onToggleAccount={toggleCompareAccount}
            onClose={() => handleCompareOpenChange(false)}
            validationMessage={compareMessage}
          />
        </DialogContent>
      </Dialog>

      <DeleteModal
        isOpen={Boolean(watchlistToDelete)}
        toggle={() => {
          if (deleteWatchListLoading) return;
          setWatchlistToDelete(null);
        }}
        title="Delete Watchlist"
        entityName={watchlistToDelete?.watch_list_name || "this watchlist"}
        entityDetails={
          watchlistToDelete
            ? `${getWatchListAccountCount(watchlistToDelete)} ${
                getWatchListAccountCount(watchlistToDelete) === 1
                  ? "Account"
                  : "Accounts"
              }`
            : undefined
        }
        onConfirm={handleDeleteWatchlistConfirm}
        loading={deleteWatchListLoading}
      />

      <DeleteModal
        isOpen={Boolean(monitoredAccountToDelete)}
        toggle={() => {
          if (deleteMonitoredLoading) return;
          setMonitoredAccountToDelete(null);
        }}
        title="Delete Monitored Account"
        entityName={monitoredAccountToDelete?.name || "this account"}
        onConfirm={handleDeleteMonitoredConfirm}
        loading={deleteMonitoredLoading}
      />
    </>
  );
};

export default MonitoredWatchlistSidebar;
